import React, { FC, useEffect, useState } from 'react'
import { pathOr } from 'ramda'
import { FastField } from 'formik'
import { TextField, FormField, TextInput, Heading } from '../..'
import { PartySearch } from '../../../containers'
import { formUtils } from '../../../../utils'
import { TextFieldTypes } from 'components/atoms/TextField/text-field.types'
import { $TSFixMe } from 'types/ts-migrate'
import PhoneField from 'views/components/atoms/PhoneField/PhoneField'
import { extractDialCode } from 'utils/formUtils'
import { createPhoneFieldChangeHandler, hasNumberBeyondDialCode } from './utils'
import styles from './FormGroups.module.scss'

type TProps = {
  values?: $TSFixMe
  requiredFields?: string[]
  tags?: $TSFixMe
  handleChange?: (e: $TSFixMe) => void
  handleBlur?: (e: $TSFixMe) => void
  handleSelect?: (e: $TSFixMe) => void
  setFieldValue?: $TSFixMe
  errors?: $TSFixMe
  touched?: $TSFixMe
  allowPartySearch?: boolean
  disableFields?: $TSFixMe
  submitCount?: number
  companiesOnly?: boolean
}

const EntityDetails: FC<TProps> = ({
  values,
  requiredFields = [],
  tags,
  handleChange,
  handleBlur,
  handleSelect,
  setFieldValue,
  errors,
  touched,
  allowPartySearch = true,
  disableFields = false,
  submitCount,
  companiesOnly = false,
}: $TSFixMe) => {
  const getFieldError = formUtils.getFieldError(submitCount, touched, errors)
  const getFieldValue = (name: any) => pathOr('', [name], values)
  const isRequired = (name: string) => requiredFields.includes(name)

  const cellNumber = getFieldValue('cellNumber')
  const cellNumberReplaced = cellNumber.replace(/\s/g, '')
  const telNumber = getFieldValue('telNumber')
  const telNumberReplaced = telNumber.replace(/\s/g, '')

  const [cellDialCode, setCellDialCode] = useState(() => extractDialCode(cellNumber))
  const [telDialCode, setTelDialCode] = useState(() => extractDialCode(telNumber))

  // Update dial codes when phone numbers change (e.g., when loading a contact)
  useEffect(() => {
    const newCellDialCode = extractDialCode(cellNumber)
    if (cellNumber && newCellDialCode !== cellDialCode) {
      setCellDialCode(newCellDialCode)
    }
  }, [cellNumber])

  useEffect(() => {
    const newTelDialCode = extractDialCode(telNumber)
    if (telNumber && newTelDialCode !== telDialCode) {
      setTelDialCode(newTelDialCode)
    }
  }, [telNumber])

  useEffect(() => {
    if (telNumber) {
      const dialCodeFromValue = extractDialCode(telNumber)
      if (!hasNumberBeyondDialCode(telNumber, dialCodeFromValue)) {
        setFieldValue('telNumber', '')
      }
    }
  }, [telNumber])

  return (
    <div className={styles.root}>
      <Heading as="h3" size="a5" spacing="small">
        Entity details
      </Heading>
      <div className="rbn--row">
        <div className="rbn--col-md-4">
          <FormField>
            <FastField
              name="companyName"
              label="Name"
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={disableFields}
              render={({ field }: any) =>
                allowPartySearch ? (
                  <PartySearch
                    {...field}
                    includeIdentification
                    label="Name"
                    required={isRequired('companyName')}
                    disabled={disableFields}
                    onResultSelect={handleSelect}
                    tags={tags}
                    value={getFieldValue('companyName')}
                    error={getFieldError('companyName')}
                    limitResults={companiesOnly ? 'company' : null}
                  />
                ) : (
                  <TextField
                    {...field}
                    inputComponent={
                      <TextInput
                        id={field.name}
                        name={field.name}
                        type={TextFieldTypes.text}
                        value={getFieldValue('companyName')}
                        disabled={disableFields}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    }
                    label="Name"
                    required={isRequired('companyName')}
                    error={getFieldError('companyName')}
                  />
                )
              }
            />
          </FormField>
        </div>
        <div className="rbn--col-md-4">
          <FormField>
            <TextField
              inputComponent={
                <TextInput
                  type={TextFieldTypes.text}
                  name="tradingAs"
                  value={getFieldValue('tradingAs')}
                  disabled={disableFields}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              }
              label="Trading name"
              error={getFieldError('tradingAs')}
            />
          </FormField>
        </div>
      </div>
      <div className="rbn--row">
        <div className="rbn--col-md-4">
          <FormField>
            <TextField
              inputComponent={
                <TextInput
                  type={TextFieldTypes.text}
                  name="vatNumber"
                  value={getFieldValue('vatNumber')}
                  disabled={disableFields}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              }
              label="VAT number"
              labelHint="Must be a valid SARS VAT vendor number"
              error={getFieldError('vatNumber')}
            />
          </FormField>
        </div>
        <div className="rbn--col-md-4">
          <FormField>
            <TextField
              inputComponent={
                <TextInput
                  type={TextFieldTypes.text}
                  name="companyRegistration"
                  value={getFieldValue('companyRegistration')}
                  disabled={disableFields}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              }
              label="Registration number"
              labelHint="Company: CCYY/*/**, Trust: IT or MT*/CCYY(*), Sectional Title: SS*/CCYY"
              error={getFieldError('companyRegistration')}
            />
          </FormField>
        </div>
      </div>
      <section className={styles['section-divider']}>
        <Heading as="h3" size="a5" spacing="small">
          Contact details
        </Heading>
        <div className="rbn--row">
          <div className="rbn--col-md-4">
            <FormField>
              <TextField
                inputComponent={
                  <TextInput
                    type={TextFieldTypes.email}
                    name="emailAddress"
                    value={getFieldValue('emailAddress')}
                    disabled={disableFields}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                }
                label="Email address"
                required={isRequired('emailAddress')}
                error={getFieldError('emailAddress')}
              />
            </FormField>
          </div>
          <div className="rbn--col-md-4">
            <FormField>
              <TextField
                inputComponent={
                  <TextInput
                    type={TextFieldTypes.email}
                    name="alternativeEmailAddress"
                    value={getFieldValue('alternativeEmailAddress')}
                    disabled={disableFields}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                }
                label="Alternative email address"
                labelHint="This email address will be CC'd on any tenant or owner communication sent to this contact"
                error={getFieldError('alternativeEmailAddress')}
              />
            </FormField>
          </div>
        </div>
        <div className="rbn--row">
          <div className="rbn--col-md-4">
            <FormField>
              <TextField
                inputComponent={
                  <PhoneField
                    defaultCountry="za"
                    value={
                      cellNumber.includes(cellDialCode) ? cellNumber : [cellDialCode, cellNumberReplaced].join(' ')
                    }
                    disabled={disableFields}
                    onChange={createPhoneFieldChangeHandler(
                      'cellNumber',
                      setFieldValue,
                      setCellDialCode,
                      hasNumberBeyondDialCode,
                    )}
                  />
                }
                label="Contact number"
                error={getFieldError('cellNumber')}
              />
            </FormField>
          </div>
          <div className="rbn--col-md-4">
            <FormField>
              <TextField
                inputComponent={
                  <PhoneField
                    defaultCountry="za"
                    value={telNumber.includes(telDialCode) ? telNumber : [telDialCode, telNumberReplaced].join(' ')}
                    disabled={disableFields}
                    onChange={createPhoneFieldChangeHandler(
                      'telNumber',
                      setFieldValue,
                      setTelDialCode,
                      hasNumberBeyondDialCode,
                    )}
                  />
                }
                label="Alternative contact number"
                error={getFieldError('telNumber')}
              />
            </FormField>
          </div>
        </div>
        <div className="rbn--row">
          <div className="rbn--col-md-4">
            <FormField>
              <TextField
                inputComponent={
                  <TextInput
                    type={TextFieldTypes.text}
                    name="contactPersonFirstName"
                    value={getFieldValue('contactPersonFirstName')}
                    disabled={disableFields}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                }
                label="Contact person first name"
                error={getFieldError('contactPersonFirstName')}
              />
            </FormField>
          </div>
          <div className="rbn--col-md-4">
            <FormField>
              <TextField
                inputComponent={
                  <TextInput
                    type={TextFieldTypes.text}
                    name="contactPersonLastName"
                    value={getFieldValue('contactPersonLastName')}
                    disabled={disableFields}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                }
                label="Contact person last name"
                error={getFieldError('contactPersonLastName')}
              />
            </FormField>
          </div>
        </div>
      </section>
    </div>
  )
}

export default EntityDetails
